import { retireTransferredInventory } from '../shared/inventory-applied-truth';
import type { AppliedFrame } from '../janus-engine/transport/applied-frame';
import './trade-exchange-truth.js';
// inventory-trade-truth.ts — Lane INV2 S2 / S3 / S4 (2026-09-27): the EXCHANGE frame subscriber (`janus_inventory_trade`).
//
// The browser pushes inventory/status/charge deltas before saving a trade, haggle or training.
// This subscriber settles transferred list entries and merchant_favor in held copies and open panels.
import { pricingFavor, favorTier } from '../janus-engine/inventory/trade-exchange.js';
import { EXCHANGE_SCHEMA, favorPrices, type ExchangeKind } from '../janus-engine/inventory/trade-adjust.js';

const upper = (v: unknown): string => String(v ?? '').trim().toUpperCase();
type Row = Record<string, unknown>;

export interface InventoryTradeWireFrame {
  schema: typeof EXCHANGE_SCHEMA;
  applied_frame?: AppliedFrame;
  operation_id: string;
  encounter_id: string;
  kind: ExchangeKind;
  actor_id: string;
  counterpart_id: string;
  merchant_favor?: number | null;
  pricing_favor?: number;
  reason?: string;
  provisional?: boolean;
  committed?: boolean;
  correction?: boolean;
}

const seen = new Map<string, Set<string>>();
function noteSeen(operationId: string, kind: string): boolean {
  const kinds = seen.get(operationId) ?? new Set<string>();
  if (kinds.has(kind)) return false;
  kinds.add(kind); seen.set(operationId, kinds);
  if (seen.size > 256) seen.delete(seen.keys().next().value!);
  return true;
}

/** Set (or, at null / 0, drop — the lean build's elision) merchant_favor on every cached copy of the counterpart row. */
function patchFavor(counterpartId: string, favor: number | null): void {
  const ft = (window as unknown as { FateTide?: { casterCache?: unknown; encounterCache?: unknown } }).FateTide;
  const apply = (row: Row | null | undefined): void => {
    const ec = row?.encounter_creatures as Row | undefined;
    if (!ec || upper(ec.encounter_creature_id) !== counterpartId) return;
    if (favor === null || favor === 0) delete ec.merchant_favor; else ec.merchant_favor = favor;
  };
  if (ft?.casterCache instanceof Map) for (const row of ft.casterCache.values()) apply(row as Row);
  if (ft?.encounterCache instanceof Map) {
    for (const enc of ft.encounterCache.values()) for (const row of ((enc as Row)?.encounter_creatures_arr as Row[] | undefined) ?? []) apply(row);
  }
}

export function handleInventoryTradeTruth(frame: InventoryTradeWireFrame): void {
  if (frame?.schema !== EXCHANGE_SCHEMA || !frame.operation_id || !frame.counterpart_id) return;
  const kind = frame.correction ? 'correction' : frame.committed ? 'committed' : 'provisional';
  if (!noteSeen(frame.operation_id, kind)) return;
  const counterpart = upper(frame.counterpart_id);
  if (kind === 'committed') {
    if (frame.applied_frame) retireTransferredInventory(frame.applied_frame);
    const favor = frame.merchant_favor === null || frame.merchant_favor === undefined ? null : Number(frame.merchant_favor);
    patchFavor(counterpart, Number.isFinite(favor) ? favor : null);
  }
  // Frames carry raw SQL favor. Each open panel applies its held campaign economy in the browser.
  if (kind !== 'provisional' && Object.prototype.hasOwnProperty.call(frame, 'merchant_favor')) {
    type Panel = { active?: boolean; merchantEcId?: string; trainerEcId?: string; merchantFavor: number | null; economy: unknown;
      favor: number; buyP?: number; sellP: number; tierDescription: string };
    const browser = window as unknown as { tradeState?: Panel | null; trainState?: Panel | null; ftExchangeFavorChanged?: () => void };
    for (const st of [browser.tradeState, browser.trainState]) if (st?.active && upper(st.merchantEcId ?? st.trainerEcId) === counterpart) {
      st.merchantFavor = frame.merchant_favor == null ? null : Number(frame.merchant_favor);
      st.favor = pricingFavor(st.merchantFavor, st.economy); Object.assign(st, favorPrices(st.favor)); st.tierDescription = favorTier(st.favor);
    }
    browser.ftExchangeFavorChanged?.();
  }
  window.dispatchEvent(new CustomEvent('ft:inventory-trade-truth', { detail: { kind, frame } }));
}
